import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { signIn, signUp, signOut, getCurrentUser, getSession } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';

export function useAuth() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  // 获取当前用户
  const { data: user, isError: userError } = useQuery({
    queryKey: ['user'],
    queryFn: getCurrentUser,
    retry: false,
  });

  // 获取当前会话
  const { data: session, isError: sessionError } = useQuery({
    queryKey: ['session'],
    queryFn: getSession,
    retry: false,
  });

  const isAuthenticated = !!session;

  // 登出mutation
  const logoutMutation = useMutation({
    mutationFn: signOut,
    onSuccess: () => {
      // 清除缓存
      queryClient.clear();
      // 重置认证状态
      queryClient.setQueryData(['user'], null);
      queryClient.setQueryData(['session'], null);
      // 重定向到首页
      navigate('/', { replace: true });
    },
    onError: (error) => {
      console.error('Logout error:', error);
    },
  });

  useEffect(() => {
    console.log('Auth state:', { user, session, isAuthenticated });
    
    // 监听认证状态变化
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event, session);
      
      if (event === 'SIGNED_IN') {
        console.log('User signed in, refreshing data...');
        await queryClient.invalidateQueries({ queryKey: ['user'] });
        await queryClient.invalidateQueries({ queryKey: ['session'] });
        await queryClient.invalidateQueries({ queryKey: ['posts'] });
      } else if (event === 'SIGNED_OUT') {
        console.log('User signed out, clearing cache...');
        queryClient.clear();
        navigate('/', { replace: true });
      }
    });

    // 检查初始认证状态
    const checkInitialAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          console.log('Found existing session:', session);
          await queryClient.invalidateQueries({ queryKey: ['user'] });
          await queryClient.invalidateQueries({ queryKey: ['session'] });
        }
      } catch (error) {
        console.error('Error checking initial auth:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkInitialAuth();

    return () => {
      subscription.unsubscribe();
    };
  }, [queryClient, navigate]);

  const loginMutation = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      signIn(email, password),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['user'] });
      await queryClient.invalidateQueries({ queryKey: ['session'] });
      navigate('/admin');
    },
  });

  const registerMutation = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      signUp(email, password),
    onSuccess: () => {
      navigate('/auth/login');
    },
  });

  return {
    user,
    session,
    isAuthenticated,
    isLoading,
    login: loginMutation.mutate,
    register: registerMutation.mutate,
    logout: () => logoutMutation.mutate(),
    loginError: loginMutation.error,
    registerError: registerMutation.error,
  };
}